import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { HoverHintContent, HoverHintTrigger } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import type { MrpSkuDetailTab, MrpSkuHoverHintFields } from "@/lib/mrp";
import { cn } from "@/lib/utils";

import { MrpSkuCatalogHoverHintBody } from "./MrpSkuCatalogHoverHintBody";
import { MrpSkuWithUnitLabel } from "./MrpSkuWithUnitLabel";

/** Deferred batch fetch for MRP catalog hover hints (category, description, variant, unit). */
export function useDeferredMrpSkuHoverHints(skus: string[], scope: string) {
  const uniqueSkus = useMemo(
    () => [...new Set(skus.map((sku) => sku.trim()).filter((sku) => sku.length > 0))],
    [skus],
  );
  const [hintsFetchEnabled, setHintsFetchEnabled] = useState(false);
  const enableHintsFetch = useCallback(() => {
    setHintsFetchEnabled(true);
  }, []);

  useEffect(() => {
    if (uniqueSkus.length === 0) {
      return;
    }
    const idleId =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback(() => setHintsFetchEnabled(true))
        : window.setTimeout(() => setHintsFetchEnabled(true), 0);
    return () => {
      if (typeof cancelIdleCallback === "function" && typeof idleId === "number") {
        cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId as number);
      }
    };
  }, [uniqueSkus]);

  const skuHoverHintsQuery = useQuery<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }, ApiError>({
    queryKey: ["mrp", "sku-hover-hints", scope, uniqueSkus.join("\n")],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: uniqueSkus.join(",") },
      }),
    enabled: hintsFetchEnabled && uniqueSkus.length > 0,
    staleTime: 60_000,
  });

  const hintsLoading =
    hintsFetchEnabled && (skuHoverHintsQuery.isPending || skuHoverHintsQuery.isFetching);

  return {
    skuHoverHints: skuHoverHintsQuery.data?.sku_hover_hints,
    hintsLoading,
    enableHintsFetch,
  };
}

type MrpSkuHoverPeekProps = {
  sku: string;
  hint?: MrpSkuHoverHintFields;
  hintsLoading?: boolean;
  onHoverIntent?: () => void;
  tab?: MrpSkuDetailTab;
  className?: string;
  /** Extra classes for the navigable SKU link when `tab` is set (merged after defaults). */
  linkClassName?: string;
  children?: ReactNode;
  showUnitLabel?: boolean;
  block?: boolean;
  /** Use {@link FLOATING_POPOVER_Z_INDEX} inside hub modals so the peek renders above the modal shell. */
  portalZIndex?: number;
};

/**
 * SKU label with the standard MRP catalog hover peek (category, description, variant).
 * Use `tab` for a navigable link trigger; omit `tab` for hover-only (e.g. inside queue row buttons).
 */
export function MrpSkuHoverPeek({
  sku,
  hint,
  hintsLoading = false,
  onHoverIntent,
  tab,
  className,
  linkClassName,
  children,
  showUnitLabel = false,
  block = false,
  portalZIndex,
}: MrpSkuHoverPeekProps): ReactNode {
  const trimmed = sku.trim();
  if (trimmed === "") {
    return <span className={className}>—</span>;
  }

  const label =
    children ??
    (showUnitLabel ? (
      <MrpSkuWithUnitLabel sku={trimmed} unit={hint?.mv_unit} />
    ) : (
      trimmed
    ));

  const peekBody = (
    <MrpSkuCatalogHoverHintBody sku={trimmed} hint={hint} loading={hintsLoading} />
  );

  if (tab != null) {
    return (
      <HoverHintContent
        hoverHint={peekBody}
        block
        className={className}
        portalZIndex={portalZIndex}
        cursor="default"
        openOnPointerActivate
      >
        <MrpSkuDetailLink
          sku={trimmed}
          tab={tab}
          className={cn("font-mono", linkClassName)}
          onPointerEnter={onHoverIntent}
          onPointerDown={onHoverIntent}
          onFocus={onHoverIntent}
        >
          {label}
        </MrpSkuDetailLink>
      </HoverHintContent>
    );
  }

  return (
    <HoverHintTrigger
      align="start"
      block={block}
      className={className}
      portalZIndex={portalZIndex}
      trigger={
        <span
          className={cn(
            "cursor-default font-mono text-sky-800 underline decoration-sky-800/40",
            className,
          )}
          onPointerEnter={onHoverIntent}
          onFocus={onHoverIntent}
        >
          {label}
        </span>
      }
    >
      {peekBody}
    </HoverHintTrigger>
  );
}
